// @ts-strict-ignore
import { IArticlePrepareProcessor } from './i-article-prepare.processor';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { SuitableConnectionsSetVO } from '../../model/vo/suitable-connections-set.vo';
import { ConnectionCubeSearchVector } from '@article/components/connections/connection-creation/search/connection-cube-search-vector';
import { ArticlePrepareData } from './insert/article-prepare-data';
import { ArticleAccessoryManager } from '@article/manager/article-accessory.manager';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ConnectionCubeSearchHelper } from '@article/components/connections/connection-creation/search/connection-cube-search.helper';
import { Dictionary } from 'typescript-collections';
import { InsertArticlePrepareData } from './insert/insert-article-prepare-data';
import { MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { Injectable } from '@angular/core';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';

@Injectable({ providedIn: 'root' })
export class EndPlateArticlePrepareProcessor implements IArticlePrepareProcessor {
  public defaultProcessor: IArticlePrepareProcessor;

  constructor(
    private articleAccessoryManager: ArticleAccessoryManager,
    private mountingRailHelper: MountingRailHelper,
    private containerService: ContainerService,
    private articleConnectionService: ArticleConnectionService
  ) {}

  public process(
    article: ArticleVO,
    givingConnectionSets: SuitableConnectionsSetVO[],
    acceptingCubeVectors: ConnectionCubeSearchVector[],
    checkFreeConnection: boolean
  ): ArticlePrepareData[] {
    let articlePrepareDatas: ArticlePrepareData[] = [];
    if (this.defaultProcessor) {
      articlePrepareDatas = this.defaultProcessor.process(article, givingConnectionSets, acceptingCubeVectors, checkFreeConnection);
    }
    if (articlePrepareDatas.length === 0) {
      articlePrepareDatas = this.processEndPlatesByMountingRail(article, givingConnectionSets, acceptingCubeVectors);
    }
    return articlePrepareDatas;
  }

  private processEndPlatesByMountingRail(
    article: ArticleVO,
    givingConnectionSets: SuitableConnectionsSetVO[],
    acceptingCubeVectors: ConnectionCubeSearchVector[]
  ): ArticlePrepareData[] {
    const mountingRailUsageId: string = this.getMountingRailUsageId();
    if (!mountingRailUsageId) {
      return [];
    }
    const articlePrepareDatas: ArticlePrepareData[] = [];
    for (const connectionSet of givingConnectionSets) {
      const cubeVector: ConnectionCubeSearchVector = ConnectionCubeSearchHelper.findCubeVectorById(
        connectionSet.searchVectorId,
        acceptingCubeVectors
      );
      const usageIdsByVector: string[] = this.collectSuitableUsageIdsBySearchVector(cubeVector, article.identity);
      const connectionNameToRotationDeltaToArticleUsageIdsMap: Dictionary<
        string,
        Dictionary<string, string[]>
      > = this.splitArticleUsageIdsAsMappingDictionary(usageIdsByVector, article);
      for (const connectionName of connectionNameToRotationDeltaToArticleUsageIdsMap.keys()) {
        const rotationDeltaToArticleUsageIdsMap: Dictionary<string, string[]> =
          connectionNameToRotationDeltaToArticleUsageIdsMap.getValue(connectionName);
        for (const rotationDelta of rotationDeltaToArticleUsageIdsMap.keys()) {
          const insertPrepareData: InsertArticlePrepareData = new InsertArticlePrepareData(
            connectionSet.searchVectorId,
            connectionSet.connectionNames
          );
          insertPrepareData.acceptingIdentityToInsert = MetaElementUsageIdentityVO.create(mountingRailUsageId, connectionName);
          insertPrepareData.neighborArticleUsageIds = rotationDeltaToArticleUsageIdsMap.getValue(rotationDelta);
          insertPrepareData.rotationDelta = Number(rotationDelta);
          articlePrepareDatas.push(insertPrepareData);
        }
      }
    }
    return articlePrepareDatas;
  }

  private getMountingRailUsageId(): string {
    const mountingRailUsage: ArticleUsageVO = this.mountingRailHelper.getMountingRailUsage();
    return mountingRailUsage == null ? null : mountingRailUsage.id;
  }

  private splitArticleUsageIdsAsMappingDictionary(articleUsageIds: string[], article: ArticleVO): Dictionary<string, Dictionary<string, string[]>> {
    const connectionNameToRotationDeltaToArticleUsageIdsMap: Dictionary<string, Dictionary<string, string[]>> = new Dictionary<
      string,
      Dictionary<string, string[]>
    >();
    if (!articleUsageIds) {
      return connectionNameToRotationDeltaToArticleUsageIdsMap;
    }
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      if (!this.articleConnectionService.isArticleConnectedToMountingRail(articleContainer)) {
        continue;
      }
      const parentIdentity: MetaElementUsageIdentityVO = ArticleUsageUtils.getFirstBackwardConnectedConnectionUsageId(articleContainer.articleUsage);
      const rotationDelta: number = this.articleAccessoryManager.getRotationDelta(articleContainer.article.identity, article.identity);

      if (!connectionNameToRotationDeltaToArticleUsageIdsMap.containsKey(parentIdentity.metaElementName)) {
        connectionNameToRotationDeltaToArticleUsageIdsMap.setValue(parentIdentity.metaElementName, new Dictionary<string, string[]>());
      }
      const rotationDeltaToArticleUsageIdsMap: Dictionary<string, string[]> = connectionNameToRotationDeltaToArticleUsageIdsMap.getValue(
        parentIdentity.metaElementName
      );
      if (!rotationDeltaToArticleUsageIdsMap.containsKey(rotationDelta.toString())) {
        rotationDeltaToArticleUsageIdsMap.setValue(rotationDelta.toString(), []);
      }
      const usageIds: string[] = rotationDeltaToArticleUsageIdsMap.getValue(rotationDelta.toString());
      usageIds.push(articleUsageId);
    }
    return connectionNameToRotationDeltaToArticleUsageIdsMap;
  }

  private collectSuitableUsageIdsBySearchVector(cubeVector: ConnectionCubeSearchVector, accessoryArticleId: number): string[] {
    if (!cubeVector) {
      return [];
    }
    const usageIds: string[] = [];
    const suitableArticleIds: number[] = this.collectSuitableArticleIds(cubeVector.sortedCubesSearchRequirements, accessoryArticleId);
    if (suitableArticleIds.length === 0) {
      return [];
    }
    for (const cubeSearchRequirements of cubeVector.sortedCubesSearchRequirements) {
      if (suitableArticleIds.indexOf(cubeSearchRequirements.articleId) === -1) {
        continue;
      }
      if (usageIds.indexOf(cubeSearchRequirements.articleUsageId) === -1) {
        usageIds.push(cubeSearchRequirements.articleUsageId);
      }
    }
    return usageIds;
  }

  private collectSuitableArticleIds(cubesRequirements: ConnectionCubeSearchRequirements[], accessoryArticleId: number): number[] {
    const processedArticleIds: number[] = [];
    const suitableArticleIds: number[] = [];
    for (const cubeSearchRequirements of cubesRequirements) {
      if (processedArticleIds.indexOf(cubeSearchRequirements.articleId) >= 0) {
        continue;
      }
      if (this.articleAccessoryManager.isAccessoryArticleExists(cubeSearchRequirements.articleId, accessoryArticleId)) {
        suitableArticleIds.push(cubeSearchRequirements.articleId);
      }
      processedArticleIds.push(cubeSearchRequirements.articleId);
    }
    return suitableArticleIds;
  }
}
